/**
 * Traduce los errores de Supabase Auth al crear o cambiar la contraseña a
 * un mensaje que diga qué pasó y qué hacer (convención "mensajes de error
 * dicientes", CLAUDE.md).
 *
 * Antes, todo lo que no fuera "token expirado" caía en "No pudimos activar
 * tu cuenta. Intenta de nuevo en unos minutos": el 7-sep-2026 el founder
 * tecleó una contraseña que no cumplía la política del proyecto y el
 * mensaje lo mandó a esperar en vez de decirle qué le faltaba (agregar un
 * carácter la arregló). Supabase sí dice qué falta — solo había que
 * traducirlo.
 *
 * `recoveryHint` marca los casos en que reintentar no sirve porque la
 * sesión del enlace ya no está: la salida self-service es pedir un enlace
 * nuevo desde "¿Olvidaste tu contraseña?" con el mismo correo. Ese flujo
 * ya existe, lleva Turnstile y no necesita a soporte.
 */

export interface AuthErrorLike {
  message: string;
  code?: string;
  status?: number;
  /** `AuthWeakPasswordError.reasons`: "length" | "characters" | "pwned". */
  reasons?: string[];
}

export interface PasswordErrorCopy {
  message: string;
  /** True cuando lo que ayuda es pedir un enlace nuevo, no reintentar. */
  recoveryHint: boolean;
}

/**
 * Clases de caracteres tal como Supabase las enumera en el mensaje de
 * `weak_password` ("Password should contain at least one character of
 * each: abcdefghijklmnopqrstuvwxyz, ABCDEFGHIJKLMNOPQRSTUVWXYZ, 0123456789,
 * !@#$%^&*()_+-=[]{};'\\:\"|<>?,./`~."). Se detectan por contenido para no
 * depender de cómo esté configurada la política: el mensaje solo lista las
 * que el proyecto exige.
 */
const CLASES_DE_CARACTERES: ReadonlyArray<readonly [needle: string, etiqueta: string]> = [
  ["abcdefghijklmnopqrstuvwxyz", "minúsculas"],
  ["ABCDEFGHIJKLMNOPQRSTUVWXYZ", "mayúsculas"],
  ["0123456789", "números"],
  ["!@#$%^&*", "símbolos (por ejemplo ! # $ %)"],
];

const SESION_PERDIDA = new Set(["session_not_found", "session_expired", "bad_jwt", "otp_expired"]);

export function describePasswordError(error: AuthErrorLike, minLength: number): PasswordErrorCopy {
  const code = error.code ?? "";
  const message = error.message ?? "";
  const lower = message.toLowerCase();

  if (code === "weak_password" || lower.startsWith("password should")) {
    return { message: describeWeakPassword(error, minLength), recoveryHint: false };
  }
  if (code === "same_password" || (lower.includes("same") && lower.includes("password"))) {
    return { message: "Elige una contraseña distinta a la anterior.", recoveryHint: false };
  }
  if (code === "over_request_rate_limit" || error.status === 429 || lower.includes("rate limit")) {
    return {
      message: "Hiciste varios intentos seguidos. Espera un minuto y vuelve a intentar.",
      recoveryHint: false,
    };
  }
  const tokenVencido =
    lower.includes("token") && (lower.includes("expired") || lower.includes("invalid"));
  if (
    SESION_PERDIDA.has(code) ||
    error.status === 401 ||
    error.status === 403 ||
    tokenVencido ||
    lower.includes("session")
  ) {
    return {
      message: "Tu enlace ya no está activo: puede haber vencido o haberse usado antes.",
      recoveryHint: true,
    };
  }
  return {
    message: "No pudimos guardar la contraseña. Vuelve a intentarlo en un momento.",
    recoveryHint: true,
  };
}

function describeWeakPassword(error: AuthErrorLike, minLength: number): string {
  const message = error.message ?? "";
  const lower = message.toLowerCase();
  const filtrada =
    error.reasons?.includes("pwned") ||
    lower.includes("pwned") ||
    lower.includes("known to be weak") ||
    lower.includes("easy to guess");
  if (filtrada) {
    return "Esa contraseña aparece en filtraciones de datos conocidas. Elige una que no uses en otro sitio.";
  }

  const largo = /at least (\d+) characters/i.exec(message);
  const clases = CLASES_DE_CARACTERES.filter(([needle]) => message.includes(needle)).map(
    ([, etiqueta]) => etiqueta,
  );

  if (largo && clases.length > 0) {
    return `La contraseña necesita al menos ${largo[1]} caracteres e incluir ${enumerar(clases)}.`;
  }
  if (largo) {
    return `La contraseña necesita al menos ${largo[1]} caracteres.`;
  }
  if (clases.length > 0) {
    return `La contraseña necesita incluir ${enumerar(clases)}.`;
  }
  return `La contraseña necesita al menos ${minLength} caracteres e incluir letras, números y símbolos.`;
}

function enumerar(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} y ${items[items.length - 1]}`;
}
